import moment from 'moment'
import { Card, Text, Button, Dialog, Portal } from 'react-native-paper'
import { View, Pressable, ScrollView } from 'react-native'
import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import Row from '../../../../@ui/containers/grid/row'
import Column from '../../../../@ui/containers/grid/column'
import { createStyle } from '../../../../contexts/global-styles.provider'
import {
  Billing,
  NodeTierDetail,
  TierUpgradePayload,
  TierDowngradePayload,
  NewNodeTierPayload,
  RenovateNodeTierPayload,
} from '../../../../generated/graphql'
import { useCurrency } from '../../payment/payment.utils'
import { StatusBadge } from '../../../../@ui/badges'
import { PaymentChannelModal } from '../../payment/payment-channel.modal'
import { TierItem } from './tier-breakdown.components'

interface BillingCardProps {
  billing: Billing
}

const translateRenovateDescription = (
  payload: RenovateNodeTierPayload
): string => {
  const { t } = useTranslation('billing')
  if (!payload?.month) return ''
  return t('description.renovate_node_tier', { month: payload.month })
}

const translateNewNodeTierDescription = (
  payload: NewNodeTierPayload | null | undefined
): string => {
  const { t } = useTranslation('billing')
  const { t: tTiers } = useTranslation('tiers')
  if (!payload) return ''
  return t('description.new_node_tier', {
    tierName: tTiers(`tier-name.${payload.tierName}`) || '',
    seats: payload.seats || 0,
    nodeName: payload.nodeName || 'Unnamed',
    nodeType: payload.isPrivate ? t('private') : t('public'),
  })
}

const translateTierUpgradeDescription = (
  payload: TierUpgradePayload | null | undefined
): string => {
  const { t } = useTranslation('billing')
  const { t: tTiers } = useTranslation('tiers')
  if (!payload) return ''
  return t('description.tier_upgrade', {
    tierName: tTiers(`tier-name.${payload.tierName}`) || '',
    fromSeats: payload.fromSeats || 0,
    toSeats: payload.toSeats || 0,
  })
}

const translateTierDowngradeDescription = (
  payload: TierDowngradePayload | null | undefined
): string => {
  if (!payload) return ''
  const { t } = useTranslation('billing')
  const { t: tTiers } = useTranslation('tiers')
  return t('description.tier_downgrade', {
    tierName: tTiers(`tier-name.${payload.tierName}`) || '',
    fromSeats: payload.fromSeats || 0,
    toSeats: payload.toSeats || 0,
  })
}

const translateDescription = (
  description: string | null | undefined,
  billing: Billing
): string => {
  if (!description) return ''

  // Check if it's a translation key (starts with billing.description.)
  if (!description.startsWith('billing.description.')) {
    return description
  }

  // Use factory approach based on billing type
  if (billing.billingType === 'renovate_node_tier') {
    return (
      translateRenovateDescription(
        billing.renovatePayload as RenovateNodeTierPayload
      ) || description
    )
  }

  if (billing.billingType === 'new_node_tier') {
    return (
      translateNewNodeTierDescription(billing.newNodeTierPayload) || description
    )
  }

  if (billing.billingType === 'tier_upgrade') {
    return (
      translateTierUpgradeDescription(billing.tierUpgradePayload) || description
    )
  }

  if (billing.billingType === 'tier_downgrade') {
    return (
      translateTierDowngradeDescription(billing.tierDowngradePayload) ||
      description
    )
  }

  // Fallback to original description
  return description
}

export const BillingCard = ({ billing }: BillingCardProps) => {
  const styles = useStyle()
  const { t } = useTranslation('billing')
  const currency = useCurrency(billing.amount)
  const [showDetailsDialog, setShowDetailsDialog] = useState(false)
  const [showPaymentModal, setShowPaymentModal] = useState(false)

  const translatedDescription = translateDescription(
    billing.description,
    billing
  )

  const parsedDate = moment(billing.createdAt).format('YYYY-MM-DD')
  const paidDate = billing.paidAt
    ? moment(billing.paidAt).format('YYYY-MM-DD HH:mm')
    : null
  const isPending = billing.status === 'pending'

  const handleCardPress = () => {
    setShowDetailsDialog(true)
  }

  const handlePayNow = () => {
    setShowDetailsDialog(false)
    setShowPaymentModal(true)
  }

  return (
    <>
      <Pressable onPress={handleCardPress}>
        <Card style={styles.card}>
          <View style={styles.cardContent}>
            <Row justifyContent={'space-between'} wrap>
              <View style={styles.leftSection}>
                <Text variant={'titleMedium'} style={styles.dateText}>
                  {parsedDate}
                </Text>
                <Text variant={'bodyMedium'} style={styles.amountText}>
                  {currency}
                </Text>
              </View>
              <View style={styles.rightSection}>
                <StatusBadge status={billing.status} />
                {translatedDescription && (
                  <Text
                    variant={'bodySmall'}
                    style={styles.description}
                    numberOfLines={2}
                    ellipsizeMode="tail"
                  >
                    {translatedDescription}
                  </Text>
                )}
              </View>
            </Row>
          </View>
        </Card>
      </Pressable>

      <Portal>
        <Dialog
          visible={showDetailsDialog}
          onDismiss={() => setShowDetailsDialog(false)}
          style={styles.dialog}
        >
          <Dialog.Title>{t('billing-details')}</Dialog.Title>
          <Dialog.Content style={styles.dialogContent}>
            <ScrollView
              style={styles.dialogScrollView}
              contentContainerStyle={styles.dialogScrollContent}
              showsVerticalScrollIndicator={true}
            >
              <Column gap={8}>
                {/* Description and Billing Type on same row */}
                {translatedDescription && billing.billingType ? (
                  <Row columnGap={16} alignItems="flex-start">
                    <View style={styles.detailField}>
                      <Text variant="labelMedium" style={styles.detailLabel}>
                        {t('description.title')}
                      </Text>
                      <Text variant="bodyMedium">{translatedDescription}</Text>
                    </View>
                    <View style={styles.detailField}>
                      <Text variant="labelMedium" style={styles.detailLabel}>
                        {t('billing-type')}
                      </Text>
                      <Text variant="bodyMedium">
                        {t(`billing-type-${billing.billingType.toLowerCase()}`)}
                      </Text>
                    </View>
                  </Row>
                ) : (
                  <>
                    {translatedDescription && (
                      <View>
                        <Text variant="labelMedium" style={styles.detailLabel}>
                          {t('description.title')}
                        </Text>
                        <Text variant="bodyMedium">
                          {translatedDescription}
                        </Text>
                      </View>
                    )}
                    {billing.billingType && (
                      <View>
                        <Text variant="labelMedium" style={styles.detailLabel}>
                          {t('billing-type')}
                        </Text>
                        <Text variant="bodyMedium">
                          {t(
                            `billing-type-${billing.billingType.toLowerCase()}`
                          )}
                        </Text>
                      </View>
                    )}
                  </>
                )}
                {/* Date and Paid Date on same row */}
                <Row columnGap={16} alignItems="flex-start">
                  <View style={styles.detailField}>
                    <Text variant="labelMedium" style={styles.detailLabel}>
                      {t('date')}
                    </Text>
                    <Text variant="bodyMedium">{parsedDate}</Text>
                  </View>
                  {paidDate && (
                    <View style={styles.detailField}>
                      <Text variant="labelMedium" style={styles.detailLabel}>
                        {t('paid-date')}
                      </Text>
                      <Text variant="bodyMedium">{paidDate}</Text>
                    </View>
                  )}
                </Row>
                {/* Payment Method and Transaction ID on same row */}
                {(billing.paymentMethod || billing.transactionId) && (
                  <Row columnGap={16} alignItems="flex-start">
                    {billing.paymentMethod && (
                      <View style={styles.detailField}>
                        <Text variant="labelMedium" style={styles.detailLabel}>
                          {t('payment-method')}
                        </Text>
                        <Text variant="bodyMedium">
                          {t(`payment-method-${billing.paymentMethod}`)}
                        </Text>
                      </View>
                    )}
                    {billing.transactionId && (
                      <View style={styles.detailField}>
                        <Text variant="labelMedium" style={styles.detailLabel}>
                          {t('transaction-id')}
                        </Text>
                        <Text variant="bodyMedium">
                          {billing.transactionId}
                        </Text>
                      </View>
                    )}
                  </Row>
                )}
                <View style={styles.totalAmountRow}>
                  <Text variant="labelMedium" style={styles.totalAmountLabel}>
                    {t('total-amount')}:
                  </Text>
                  <Text variant="bodyMedium" style={styles.totalAmount}>
                    {currency}
                  </Text>
                </View>
                {billing.billingType === 'renovate_node_tier' &&
                  billing.renovatePayload?.nodeTiers?.length && (
                    <View style={styles.tierDetailsSection}>
                      <Text variant="labelMedium" style={styles.detailLabel}>
                        {t('tier-breakdown')}
                      </Text>
                      {billing.renovatePayload.nodeTiers.map(
                        (tier: NodeTierDetail, index: number) => {
                          return <TierItem key={index} tier={tier} />
                        }
                      )}
                    </View>
                  )}
              </Column>
            </ScrollView>
          </Dialog.Content>
          <Dialog.Actions>
            {isPending && (
              <Button mode="contained" onPress={handlePayNow}>
                {t('pay-now')}
              </Button>
            )}
            <Button onPress={() => setShowDetailsDialog(false)}>
              {t('close')}
            </Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>

      <PaymentChannelModal
        onDismiss={() => {
          setShowPaymentModal(false)
        }}
        allowCoupon={false}
        billing={
          showPaymentModal
            ? {
                billingId: billing.id, // Always use UUID id for payment callbacks
                amount: billing.amount,
                basePrice: billing.amount,
              }
            : undefined
        }
      />
    </>
  )
}

const useStyle = createStyle(({ theme, isMobile }) => ({
  card: {
    marginVertical: theme.spacing(1),
    marginHorizontal: theme.spacing(2),
  },
  cardContent: {
    paddingVertical: theme.spacing(3),
    paddingHorizontal: theme.spacing(3),
  },
  leftSection: {
    flex: 1,
    gap: theme.spacing(1),
  },
  rightSection: {
    alignItems: 'flex-end',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    maxWidth: '60%',
    flexShrink: 1,
  },
  dateText: {
    fontWeight: '600',
  },
  amountText: {
    fontWeight: '500',
    fontSize: 16,
  },
  description: {
    color: theme.colors.onSurfaceVariant,
    marginTop: theme.spacing(0.5),
    textAlign: 'right',
    flexShrink: 1,
  },
  detailLabel: {
    fontWeight: '600',
    marginBottom: theme.spacing(0.5),
  },
  detailField: {
    flex: 1,
  },
  tierDetailsSection: {
    marginTop: theme.spacing(2),
    gap: theme.spacing(2),
  },
  totalAmountRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: theme.spacing(1),
  },
  totalAmountLabel: {
    fontWeight: '600',
  },
  totalAmount: {
    fontWeight: '600',
    fontSize: 18,
  },
  dialog: {
    width: '90%',
    alignSelf: 'center',
    maxHeight: '90%',
  },
  dialogContent: {
    paddingHorizontal: 0,
    paddingTop: 0,
    paddingBottom: 0,
    maxHeight: '90%',
  },
  dialogScrollView: {
    maxHeight: '90%',
  },
  dialogScrollContent: {
    paddingBottom: theme.spacing(2),
    paddingHorizontal: theme.spacing(3), // Match Dialog.Title padding
  },
}))
